import { useCallback, useEffect, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Fullscreen zoomable image viewer for listing photos. Supports pinch-to-zoom
 * and drag-to-pan on touch devices, double-tap to toggle zoom, and wheel-zoom
 * on desktop — so customers can inspect the real texture and colour of a
 * remnant before reserving. Keyboard: Esc closes, arrows change photo.
 */
export default function ImageLightbox({ photos = [], index = 0, alt = "", onClose }) {
  const [i, setI] = useState(index);
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const s = useRef({ mode: null, startDist: 0, startScale: 1, startX: 0, startY: 0, startTx: 0, startTy: 0, lastTap: 0 });

  const reset = useCallback(() => { setScale(1); setTx(0); setTy(0); }, []);
  useEffect(() => { reset(); }, [i, reset]);

  const go = useCallback((next) => {
    setI((v) => Math.max(0, Math.min(photos.length - 1, next)));
  }, [photos.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") go(i + 1);
      else if (e.key === "ArrowLeft") go(i - 1);
    };
    window.addEventListener("keydown", onKey);
    // Lock body scroll while the lightbox is open.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose, go, i]);

  const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);

  const onTouchStart = (e) => {
    const st = s.current;
    if (e.touches.length === 2) {
      st.mode = "pinch";
      st.startDist = dist(e.touches);
      st.startScale = scale;
    } else if (e.touches.length === 1) {
      const now = Date.now();
      if (now - st.lastTap < 300) {
        if (scale > 1) reset(); else setScale(2.5);
        st.lastTap = 0;
        st.mode = null;
        return;
      }
      st.lastTap = now;
      st.mode = scale > 1 ? "pan" : null;
      st.startX = e.touches[0].clientX;
      st.startY = e.touches[0].clientY;
      st.startTx = tx;
      st.startTy = ty;
    }
  };

  const onTouchMove = (e) => {
    const st = s.current;
    if (st.mode === "pinch" && e.touches.length === 2) {
      const d = dist(e.touches);
      const next = Math.max(1, Math.min(4, st.startScale * (d / (st.startDist || 1))));
      setScale(next);
    } else if (st.mode === "pan" && e.touches.length === 1) {
      setTx(st.startTx + (e.touches[0].clientX - st.startX));
      setTy(st.startTy + (e.touches[0].clientY - st.startY));
    }
  };

  const onTouchEnd = () => {
    const st = s.current;
    st.mode = null;
    if (scale <= 1) { setTx(0); setTy(0); }
  };

  const onWheel = (e) => {
    setScale((v) => Math.max(1, Math.min(4, v - e.deltaY * 0.0025)));
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 flex flex-col select-none"
      data-testid="image-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Photo viewer"
    >
      <div className="flex items-center justify-between px-4 py-3 text-white/90">
        <span className="text-sm font-medium" data-testid="lightbox-counter">
          {i + 1} / {photos.length}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="grid place-items-center w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          aria-label="Close photo viewer"
          data-testid="lightbox-close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div
        className="flex-1 overflow-hidden grid place-items-center touch-none"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onWheel={onWheel}
        onDoubleClick={() => (scale > 1 ? reset() : setScale(2.5))}
      >
        <img
          src={photos[i]}
          alt={alt}
          draggable={false}
          className="max-h-full max-w-full object-contain will-change-transform"
          style={{ transform: `translate(${tx}px, ${ty}px) scale(${scale})`, transition: s.current.mode ? "none" : "transform 0.15s ease-out" }}
          data-testid="lightbox-image"
        />
      </div>

      {photos.length > 1 && (
        <>
          <button
            type="button"
            onClick={() => go(i - 1)}
            disabled={i === 0}
            className="absolute left-3 top-1/2 -translate-y-1/2 grid place-items-center w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white disabled:opacity-30 transition-colors"
            aria-label="Previous photo"
            data-testid="lightbox-prev"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            type="button"
            onClick={() => go(i + 1)}
            disabled={i === photos.length - 1}
            className="absolute right-3 top-1/2 -translate-y-1/2 grid place-items-center w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white disabled:opacity-30 transition-colors"
            aria-label="Next photo"
            data-testid="lightbox-next"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}

      <p className="text-center text-white/50 text-xs py-3">Pinch, scroll or double-tap to zoom</p>
    </div>
  );
}
